import React from 'react'

import { isNumber } from 'lodash'
import Text from '~/views/components/ui/Text'

import { useCurrentUserContext } from '~/views/contexts/CurrentUserContext'
import LocalizedCurrencyNumber from '~/views/components/currency/LocalizedCurrencyNumber'
import styled from 'styled-components'

import WarningIconAndTooltip from './../../../WarningIconAndTooltip'
import { LoadingTextPlaceholder } from '~/views/components/ProjectOverview/components/ProjectOverviewCard'
import { useStaticLabels } from 'shared/hooks/use-static-labels'

/**
 * =====----===--=--===----=====
 * ForecastedContent
 */

const ForecastedContentContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: flex-end;
`

type ForecastedContentProps = {
  forecastedCost?: number | null
  budgetedCost?: number | null
  loading: boolean
  projectHasTasks?: boolean
}

export default function ForecastedCost({
  forecastedCost,
  budgetedCost,
  loading,
  projectHasTasks,
}: ForecastedContentProps) {
  const [getCommonLabel] = useStaticLabels(['common'])
  return (
    <ForecastedContentContainer
      data-component-name={'forecasted-cost-container'}
    >
      <span data-component-name={'forecasted-cost-label'}>
        {getCommonLabel('forecasted')}
      </span>

      <ForecastedContentValue
        forecastedCost={forecastedCost}
        budgetedCost={budgetedCost}
        loading={loading}
        projectHasTasks={projectHasTasks}
      />
    </ForecastedContentContainer>
  )
}

/**
 * =====----===--=--===----=====
 * ForecastedContentValue
 */

type ForecastedContentValueProps = {
  forecastedCost?: number | null
  budgetedCost?: number | null
  loading: boolean
  projectHasTasks?: boolean
}

function ForecastedContentValue({
  forecastedCost,
  budgetedCost,
  loading,
  projectHasTasks,
}: ForecastedContentValueProps) {
  if (loading) {
    return <LoadingTextPlaceholder />
  }

  const forecastedIsValid = isNumber(forecastedCost)
  const budgetedIsValid = isNumber(budgetedCost)

  const showErrorMessage = !forecastedIsValid || !budgetedIsValid

  return (
    <span data-component-name={'forecasted-value-container'}>
      <ForecastedValue forecastedCost={forecastedCost} />{' '}
      <DifferenceValue
        forecastedCost={forecastedCost}
        budgetedCost={budgetedCost}
      />
      {projectHasTasks && <ErrorIcon show={showErrorMessage} />}
    </span>
  )
}

/**
 * =====----===--=--===----=====
 * ForecastedValue
 */

type ForecastedValueProps = {
  forecastedCost?: number | null
}

function ForecastedValue({ forecastedCost }: ForecastedValueProps) {
  const { currentUser } = useCurrentUserContext()

  return (
    <span className={'font-bold'} data-component-name={'forecasted-value'}>
      {!isNumber(forecastedCost) ? (
        '-'
      ) : (
        <LocalizedCurrencyNumber
          number={forecastedCost as number}
          currentUser={currentUser}
        />
      )}
    </span>
  )
}

/**
 * =====----===--=--===----=====
 * DifferenceValue
 */

type DifferenceValueProps = {
  forecastedCost?: number | null
  budgetedCost?: number | null
  projectHasTasks?: boolean
}

function DifferenceValue({
  forecastedCost,
  budgetedCost,
}: DifferenceValueProps) {
  const [getCommonLabel] = useStaticLabels(['common'])
  const { currentUser } = useCurrentUserContext()

  const forecastedIsValid = isNumber(forecastedCost)
  const budgetedIsValid = isNumber(budgetedCost)
  const showDifference = forecastedIsValid && budgetedIsValid

  let textColor = 'text-gray-600'

  if (!showDifference) {
    return (
      <span
        data-component-name={'forecasted-difference-value'}
        className={`${textColor}`}
      >
        ({getCommonLabel('unavailable')})
      </span>
    )
  }

  const differenceCost = forecastedCost - budgetedCost
  const isEqual = differenceCost === 0
  const isGood = differenceCost < 0

  if (!isEqual) {
    textColor = isGood ? 'text-green-800' : 'text-red-600'
  }

  const qualifierSymbol = isEqual
    ? null
    : isGood
      ? getCommonLabel('under')
      : getCommonLabel('over')
  const prettyDifference = Math.abs(differenceCost)

  return (
    <span
      className={`${textColor}`}
      data-component-name={'forecasted-difference-value'}
    >
      (
      <LocalizedCurrencyNumber
        number={prettyDifference}
        currentUser={currentUser}
      />
      {qualifierSymbol && ' ' + qualifierSymbol})
    </span>
  )
}

/**
 * =====----===--=--===----=====
 * DefaultForecastedContentValue
 */

const TooltipContentContainer = styled.div`
  padding: 0;

  .title {
    margin: 0;
  }

  .list-container {
    margin: 0;
    padding-top: 3px;
    color: #888;
    padding-left: 2em;

    .list-item {
      margin: 0;
      font-size: 0.85em;
      line-height: 135%;
    }
  }
`

type ErrorIconProps = {
  show: boolean
}

function ErrorIcon({ show }: ErrorIconProps) {
  const [getProjectOverviewLabel] = useStaticLabels(['projectOverview'])

  if (!show) {
    return null
  }

  return (
    <>
      {' '}
      <WarningIconAndTooltip
        tooltipClassName={'forecasted-cost-warning-icon-tooltip'}
        iconClassName={'forecasted-cost-warning-icon'}
      >
        <TooltipContentContainer>
          <Text className={'title'}>
            {getProjectOverviewLabel(
              'projectOverviewForecastedUnavailableDueTo',
            )}
            :
          </Text>

          <ul className={'list-container'}>
            <li className={'list-item'}>
              {getProjectOverviewLabel(
                'projectOverviewMissingFullyLoadedCostsForUsers',
              )}
            </li>
            <li className={'list-item'}>
              {getProjectOverviewLabel(
                'projectOverviewInternalTasksNotAssigned',
              )}
            </li>
            <li className={'list-item'}>
              {getProjectOverviewLabel(
                'projectOverviewMissingEstimatedHoursOnTasks',
              )}
            </li>
          </ul>
        </TooltipContentContainer>
      </WarningIconAndTooltip>
    </>
  )
}
